import { useRouteBodyRef } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { type MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { MorphemeInfoPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";
import {
  KeyboardAvoidingView,
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewRef,
} from "@-/com.npmjs.react-native-keyboard-controller";
import { type Ref, useState } from "react";
import {
  type SectionAccordionHeaderAction,
  SectionAccordionNodes,
  SectionAccordionTab,
} from "@-/dev.improve.haniverse.components.ui.section-accordion";
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
} from "@-/com.npmjs.react-native";
import { MorphemeInfoPanelGroupedSidebar } from "#morpheme-info-panel-grouped-sidebar";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useContextSelector } from "@-/com.npmjs.use-context-selectors";

export type MorphemeInfoPanelTabContentRef = KeyboardAwareScrollViewRef;

export function MorphemeInfoPanelTabContent({
  className,
  groupTitle = "Sections",
  onScroll,
  onScrolledChange,
  ref,
  sections,
}: {
  className?: string;
  // Rail heading when several tabs' sections are stacked on one page (the Reference view).
  groupTitle?: string;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onScrolledChange?: (isScrolled: boolean) => void;
  ref?: Ref<MorphemeInfoPanelTabContentRef>;
  sections: MorphemeInfoPanelAccordionSectionDescriptor[];
}) {
  // Web gets a section rail beside one continuous scroll; native keeps the stacked collapsible
  // accordions, which are the right affordance in a single narrow column. See
  // `MorphemeInfoPanelGroupedSidebar` for why the two diverge. A single tab is one group, so the
  // rail lists that tab's sections under its own heading.
  if (Platform.OS === "web") {
    return (
      <MorphemeInfoPanelGroupedSidebar
        groups={[{ key: "tab", sections, title: groupTitle ?? "Sections" }]}
      />
    );
  }

  return (
    <MorphemeInfoPanelTabContentScroll
      className={className}
      onScroll={onScroll}
      onScrolledChange={onScrolledChange}
      ref={ref}
      sections={sections}
    />
  );
}

function MorphemeInfoPanelTabContentScroll({
  className,
  onScroll,
  onScrolledChange,
  ref: forwardedRef,
  sections,
}: {
  className?: string;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onScrolledChange?: (isScrolled: boolean) => void;
  ref?: Ref<MorphemeInfoPanelTabContentRef>;
  sections: MorphemeInfoPanelAccordionSectionDescriptor[];
}) {
  const scrollY = useRouteBodyRef("info-scroll-y", 0);
  const [initialOffset] = useState(() => ({ x: 0, y: scrollY.current }));
  const floatingTabBottomInset = useContextSelector(
    MorphemeInfoPanelContext,
    (value) => value.floatingTabBottomInset,
  );

  return (
    <KeyboardAvoidingView behavior="padding" className="flex-1">
      <SectionAccordionTab
        className={className}
        contentContainerStyle={{ paddingBottom: floatingTabBottomInset }}
        contentOffset={initialOffset}
        directionalLockEnabled
        keyboardShouldPersistTaps="handled"
        onScroll={(event) => {
          scrollY.current = event.nativeEvent.contentOffset.y;
          onScrolledChange?.(event.nativeEvent.contentOffset.y > 0);
          onScroll?.(event);
        }}
        scrollView={<KeyboardAwareScrollView ref={forwardedRef} />}
      >
        {sections.map((section) => (
          <SectionAccordionNodes
            actions={getSectionHeaderActions(section)}
            bodyKey={section.bodyKey}
            headerKey={section.headerKey}
            iconName={section.iconName}
            isLoading={section.isLoading}
            isOpen={section.isOpen}
            key={section.headerKey}
            onEndReached={section.onEndReached}
            onToggle={section.onToggle}
            title={section.title}
            trailing={
              section.trailingIconName === undefined ||
              section.headerActions !== undefined ||
              section.isLoading === true ? undefined : (
                <Icon className="text-foreground" name={section.trailingIconName} size={18} />
              )
            }
          >
            {section.isOpen ? section.renderBody() : null}
          </SectionAccordionNodes>
        ))}
      </SectionAccordionTab>
    </KeyboardAvoidingView>
  );
}

/**
 * A section's header toggles for the native accordion. Its header draws either the toggles or the
 * `trailing` node, never both, and hit-tests the toggles' strips itself — so a section with toggles
 * AND a trailing icon (Compounds' compact/detailed switch) carries the icon as its last toggle.
 */
function getSectionHeaderActions(
  section: MorphemeInfoPanelAccordionSectionDescriptor,
): readonly SectionAccordionHeaderAction[] | undefined {
  if (section.headerActions === undefined || section.trailingIconName === undefined) {
    return section.headerActions;
  }
  return [
    ...section.headerActions,
    {
      iconName: section.trailingIconName,
      label: `Toggle ${section.title} view`,
      onPress: section.onToggle,
      selected: false,
    },
  ];
}
